import React, { useEffect } from "react";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import { useBillingPermissions } from "../useBillingPermissions";
import { BILLING_PERMS } from "../billing-api";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { ErrorBar, ModeTotals, SessionChip, fmtDateTime } from "./CashCounterUI";

// ─────────────────────────────────────────────────────────────────────────────
// Screen 2 — Interim Scroll View.
// Session info · totals by payment mode · transactions snapshot.
// Actions: Print Interim (SCROLL_INTERIM_PRINT), Back.
// ─────────────────────────────────────────────────────────────────────────────

function InfoCell({ label, value }) {
  return (
    <div style={{ flex: "1 1 160px" }}>
      <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "13px", fontWeight: "700", color: C.text }}>{value}</div>
    </div>
  );
}

// Table styling is out of scope for this migration — the design spec's
// conversion table covers <input>/<select>/<label>/<button>/local Card
// wrappers only. Left as raw <table>/inline style, unchanged.
const th = { padding: "10px 12px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.05em" };
const td = { padding: "11px 12px", fontSize: "13px", color: C.text, borderTop: `1px solid ${C.border}` };

export default function InterimScrollView({ cc, onBack, onReviewFinal }) {
  const { can } = useBillingPermissions();
  const canPrint = can(BILLING_PERMS.SCROLL_INTERIM_PRINT);
  const canFinal = can(BILLING_PERMS.SCROLL_FINAL_VIEW) || can(BILLING_PERMS.SCROLL_FINAL_CLOSE);
  const { session, scroll, counterCode, busy, error, canRetry, retry, refreshInterim } = cc;
  const isOpen = session?.status === "OPEN";

  // The hook only auto-fetches once, right when the session opens (readmd G3) —
  // payments collected afterward won't show without a fresh fetch. Re-fetch every
  // time this screen mounts so "View Interim" always reflects live totals.
  useEffect(() => {
    if (session?.id) refreshInterim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <IpdButton
          variant="outlined"
          onClick={onBack}
          startIcon={<SvgIcon name="back" size={15} color={C.textSub} />}
          sx={{ color: C.textSub, backgroundColor: "transparent", borderColor: IPD_COLORS.border, "&:hover": { backgroundColor: IPD_COLORS.selectedBg } }}
        >
          Back
        </IpdButton>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <IpdButton
            variant="outlined"
            startIcon={<SvgIcon name="refresh" size={15} color={C.text} />}
            onClick={refreshInterim}
            disabled={busy || !session?.id}
          >
            Refresh
          </IpdButton>
          {canPrint && (
            <IpdButton
              variant="outlined"
              startIcon={<SvgIcon name="printer" size={15} color={C.text} />}
              onClick={cc.printInterim}
              disabled={busy || !session?.id}
            >
              Print Interim
            </IpdButton>
          )}
          {canFinal && onReviewFinal && (
            <IpdButton
              tone="teal"
              startIcon={<SvgIcon name="lock" size={15} color={IPD_COLORS.white} />}
              onClick={onReviewFinal}
              disabled={busy || !isOpen}
            >
              Final Scroll &amp; Close
            </IpdButton>
          )}
        </div>
      </div>

      <ErrorBar message={error} onRetry={canRetry ? retry : null} busy={busy} />

      <IpdSectionCard title="Session">
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <InfoCell label="Counter code" value={scroll?.counterCode || session?.counterCode || counterCode} />
          <InfoCell label="Opened by" value={scroll?.openedBy || session?.openedBy || "—"} />
          <InfoCell label="Opened at" value={fmtDateTime(scroll?.openedAt || session?.openedAt)} />
          <div style={{ flex: "1 1 120px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>Status</div>
            <SessionChip status={session?.status} />
          </div>
        </div>
      </IpdSectionCard>

      {!scroll ? (
        <IpdSectionCard>
          <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
            {session?.id ? "Loading interim totals…" : "No open session. Open one from the dashboard."}
          </p>
        </IpdSectionCard>
      ) : (
        <>
          <IpdSectionCard title="Totals by payment mode">
            <ModeTotals modes={scroll.modes} fmt={fmtINR} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", paddingTop: "12px", borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>Net collection</span>
              <span style={{ fontSize: "18px", fontWeight: "800", color: C.text }}>{fmtINR(scroll.netCollection)}</span>
            </div>
          </IpdSectionCard>

          <IpdSectionCard title={`Transactions snapshot${scroll.transactions.length ? ` (${scroll.transactions.length})` : ""}`}>
            {!scroll.transactions.length ? (
              <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
                No transactions recorded for this session yet.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Receipt</th>
                      <th style={th}>Type</th>
                      <th style={th}>Mode</th>
                      <th style={th}>Collected by</th>
                      <th style={th}>Time</th>
                      <th style={{ ...th, textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scroll.transactions.map((t) => (
                      <tr key={t.id}>
                        <td style={{ ...td, fontWeight: "700" }}>{t.receipt}</td>
                        <td style={{ ...td, color: C.textSub }}>{t.type}</td>
                        <td style={{ ...td, color: C.textSub }}>{t.mode}</td>
                        <td style={{ ...td, color: C.text }}>{t.collectedBy}</td>
                        <td style={{ ...td, color: C.textSub }}>{fmtDateTime(t.time)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: "700" }}>{fmtINR(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </IpdSectionCard>
        </>
      )}
    </div>
  );
}
